import { Alert, Button, Col, DataHeader, Form, Grid, Row } from "@payping/web-components";
import React from "react";
import { FormInstance } from "@payping/web-components/components/Form/types";
import { PhRDownloadSimple } from "@payping/icons";
import { useDownloadList } from "@/pages/Settlement/Create/downloadList";
import { RequestStatusEnum, TransferTypeEnum, requestApis } from "@payping/api";
import { getCashOutOverview, setCashOutRequestList } from "@payping/redux/actions";
import { useParams } from "react-router-dom";
import { OverviewType, SettlementCreateFormType } from "@/pages/Settlement/Create";
import { useAsync } from "react-use";
import { useAppDispatch, useAppSelector } from "@/utils";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { excelDownloadId } from "@/assets/googleTrackIds";
import ShowList from "./showList";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";

interface Props {
  form: FormInstance<SettlementCreateFormType>;
  setOverviewData: React.Dispatch<React.SetStateAction<OverviewType>>;
  setRequestTitle: React.Dispatch<React.SetStateAction<string>>;
  setRequestDescription: React.Dispatch<React.SetStateAction<string>>;
  setTransferType: React.Dispatch<React.SetStateAction<TransferTypeEnum>>;
  setRequestStatus: React.Dispatch<React.SetStateAction<RequestStatusEnum>>;
  setAmountStepThree: React.Dispatch<React.SetStateAction<number>>;
}

const ThirdStep: React.FC<Props> = (props) => {
  const { code } = useParams<{ code: string }>() as { code: string };
  const { t } = useAppTranslation();
  const dispatch = useAppDispatch();
  const {
    form,
    setOverviewData,
    setTransferType,
    setRequestDescription,
    setRequestTitle,
    setRequestStatus,
    setAmountStepThree,
  } = props;
  const { lg } = Grid.useBreakpoint();
  const { downloadList } = useDownloadList();
  const { createdRequest } = useAppSelector((state) => state.cashOuts);
  const hasEmptyFullName = createdRequest?.settlements?.some((item) => !item.fullName?.trim());

  useAsync(async () => {
    const overviewDetail = await dispatch(getCashOutOverview());
    const requestDetail = await requestApis.getRequestDetailsWithRequestCodeWithCancellationTokenGet(code);

    if (
      overviewDetail?.withdrawableBalance !== undefined &&
      requestDetail?.totalWage !== undefined &&
      requestDetail.totalAmount !== undefined
    ) {
      setOverviewData({
        balance: formatLocalizedNumber({
          value: overviewDetail.withdrawableBalance - (requestDetail.totalWage + requestDetail.totalAmount),
        }),
        total: formatLocalizedNumber({ value: requestDetail.totalAmount + requestDetail.totalWage }),
        totalAmount: formatLocalizedNumber({ value: requestDetail?.totalAmount }),
        totalWage: formatLocalizedNumber({ value: requestDetail?.totalWage ?? 0 }),
        withdrawableBalance: formatLocalizedNumber({ value: overviewDetail?.withdrawableBalance }),
      });
    }

    const response = await requestApis.getRequestDetailsWithRequestCodeWithCancellationTokenGet(code);

    setRequestTitle(response.title || "");
    setRequestDescription(response.description || "");
    setTransferType(response.transferType || TransferTypeEnum.AUTO);
    setRequestStatus(response.status || RequestStatusEnum.Incomplete);
    setAmountStepThree(response.totalAmount || 0);

    await dispatch(setCashOutRequestList({ code, settlements: response.settlements }));
  }, [code]);

  return (
    <Form form={form} className="tw-w-full" layout={"vertical"}>
      <Row gutter={[0, 16]} className={!lg ? "tw-mb-[250px] tw-w-full" : undefined}>
        {!lg ? (
          <Col xs={24}>
            <DataHeader
              filters={{
                downloadButton: (
                  <Button
                    key={1}
                    icon={<PhRDownloadSimple width={14} height={14} />}
                    onClick={() => downloadList()}
                    data-google-track={excelDownloadId}
                  >
                    {t("downloadExcel")}
                  </Button>
                ),
              }}
            />
          </Col>
        ) : null}

        {hasEmptyFullName ? (
          <Col xs={24}>
            <Alert
              type="warning"
              message={t("settlement.create.unspecifiedTransferDestination")}
              showIcon
              description={t("settlement.create.unclearInquiryDesc")}
            />
          </Col>
        ) : null}
        <Col xs={24}>
          <ShowList />
        </Col>
      </Row>
    </Form>
  );
};

export default ThirdStep;
